import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useModal } from '@/contexts/ModalContext'

import * as S from './styles'

import type {
  FilterType,
  OrderType
} from '@/types/components/CollectionsDragDrop'

export interface CollectionFiltersModalConfig {
  defaultValues: {
    filter: FilterType
    order: OrderType
  }
  changeFilters: (filter: FilterType, order: OrderType) => void
}

export interface CollectionFiltersModalProps {
  config: CollectionFiltersModalConfig
}

export const CollectionFiltersModal = ({
  config: { defaultValues, changeFilters }
}: CollectionFiltersModalProps) => {
  const [currentFilter, setCurrentFilter] = useState<FilterType>(
    defaultValues.filter
  )
  const [currentOrder, setCurrentOrder] = useState<OrderType>(
    defaultValues.order
  )

  const { t } = useTranslation()
  const { closeModal } = useModal()

  const filterOptions: Record<FilterType, string> = {
    all: t('categories.all'),
    '3d': t('labels.3d_contents'),
    slide: t('labels.slides'),
    radiology: t('modules.radiology')
  }

  const orderOptions = {
    recents: t('labels.recentsOrder'),
    alphabetical: t('labels.alphabeticalOrder'),
    contentType: t('labels.contentTypeOrder')
  }

  const onSubmit = useCallback(() => {
    changeFilters(currentFilter, currentOrder)
    closeModal()
  }, [currentFilter, currentOrder, changeFilters, closeModal])

  return (
    <S.Container>
      <S.SelectsContainer>
        <Select
          currentKey={currentOrder}
          onChange={key => setCurrentOrder(key as OrderType)}
          options={orderOptions}
          prefixLabel={`${t('labels.order')}: `}
        />
        <Select
          currentKey={currentFilter}
          onChange={key => setCurrentFilter(key as FilterType)}
          options={filterOptions}
          fixedLabel={t('labels.type')}
        />
      </S.SelectsContainer>
      <S.ButtonsContainer>
        <S.Button onClick={closeModal}>{t('buttons.cancel')}</S.Button>
        <S.Button type="submit" onClick={onSubmit}>
          {t('labels.filter')}
        </S.Button>
      </S.ButtonsContainer>
    </S.Container>
  )
}

interface SelectProps {
  currentKey: string
  onChange: (key: string) => void
  options: Record<string, string>
  fixedLabel?: string
  prefixLabel?: string
}

const Select = ({
  currentKey,
  onChange,
  options,
  fixedLabel,
  prefixLabel = ''
}: SelectProps) => {
  const [showOptions, setShowOptions] = useState(false)

  const toggleOptions = () => {
    setShowOptions(prev => !prev)
  }

  return (
    <S.SelectContainer>
      <S.SelectButton onClick={toggleOptions}>
        {fixedLabel ?? prefixLabel + options[currentKey]}
        <span id="filter-options-btn-arrow" className={'material-icons'}>
          {showOptions ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
        </span>
      </S.SelectButton>
      {showOptions && (
        <S.SelectItemsContainer>
          {Object.entries(options).map(([key, value]) => (
            <S.SelectItem key={key} onClick={() => onChange(key)}>
              <S.RadioDot isSelected={key === currentKey} />
              {value}
            </S.SelectItem>
          ))}
        </S.SelectItemsContainer>
      )}
    </S.SelectContainer>
  )
}
